/**
 * WHEN THE PARTNER'S AUDIO MAY START, SO THE EARPIECE EXISTS.
 *
 * On Android, Chromium gives its renderer's audio output stream an
 * `AudioAttributes.usage` exactly once — when it CREATES that stream — and it
 * chooses from its own communication-mode flag, which follows an active WebRTC
 * microphone capture. A stream created before the microphone is open is born
 * `USAGE_MEDIA`, and a `USAGE_MEDIA` stream follows the media strategy: with no
 * headset attached, the loudspeaker, always. Nothing the routing plugin can do
 * reaches it — `AudioManager.setCommunicationDevice()` steers the communication
 * strategy and nothing else. So the control lights, `getStatus()` agrees, and
 * the earpiece is unreachable for the whole call.
 *
 * MEASURED, on a real SM-G981U1 (Android 13), 2026-09-15, over `dumpsys audio`:
 *
 *   circle (mic opens first)      piid:8903 usage=USAGE_VOICE_COMMUNICATION
 *   1:1    (playback starts first) piid:8951 usage=USAGE_MEDIA
 *
 * Same handset, same process, minutes apart. The circle publishes its
 * microphone to Janus before it plays anybody; the 1:1 runs its handshake
 * FIRST and asks for `getUserMedia` after it, on purpose, because a slow
 * permission sheet must not hold up the handshake — so the partner can arrive
 * before the microphone has even been requested.
 *
 * WHY THE OBVIOUS FIX IS NOT ONE, and this file used to contain it. Detaching
 * and re-attaching `srcObject` after the microphone lands does NOT make the
 * platform build a new stream. Measured, in a live 1:1 with the mic open:
 *
 *   before rebuild   piid:8823 state:started usage=USAGE_MEDIA
 *   after  rebuild   piid:8823 state:started usage=USAGE_MEDIA   <- same piid
 *
 * The piid is unchanged: Chromium keeps one output stream for the renderer and
 * re-uses it. A second `new Audio()` on the same stream joins it rather than
 * creating another. The stream is only re-typed if it is fully RELEASED first —
 * every element detached, the configuration gone from `dumpsys`, which took
 * seconds of real silence to happen and then produced
 * `piid:8967 usage=USAGE_VOICE_COMMUNICATION`. Seconds of the partner going
 * quiet, once per call, is not a fix; it is a different bug.
 *
 * SO THE ORDER IS THE FIX, and this module is the one place that says what the
 * order is. The remote stream is held until the microphone is open, then
 * attached — which is exactly what the circle does by accident of its design,
 * and why the circle was always right.
 */

/**
 * How long the partner's audio may be held waiting for the microphone.
 *
 * CLASS B — the refusal has to reach the user as something other than silence.
 * If the permission sheet is declined, or the device has no usable input, the
 * microphone never opens and a gate with no deadline would mean the partner is
 * never heard at all. That trades a routing bug for a total-audio bug, which is
 * strictly worse. After the deadline the stream is attached anyway: the call is
 * audible, on the loudspeaker, exactly as it was before this fix — a worse
 * route, not no call.
 *
 * IT IS A BACKSTOP, NOT THE MECHANISM. The precise signal for "this microphone
 * is never coming" is `getUserMedia` REJECTING, and the call screen releases the
 * gate on that the moment it happens — see the `catch` in `PeerAudioRoom`'s
 * setup. What is left for a timer is only the case with no signal at all: a
 * permission sheet the person never answers, or a platform that neither
 * resolves nor rejects.
 *
 * Five seconds, and the first value here was two, which was wrong on the
 * device. The measured gap between the remote track arriving and `getUserMedia`
 * resolving is ~1.5s warm — but on a cold start (the process just launched, the
 * page just parsed) it goes past two, and a deadline that fires first attaches
 * the partner BEFORE the microphone, which is precisely the bug. A litmus run
 * on 2026-09-15 flipped between `USAGE_VOICE_COMMUNICATION` and `USAGE_MEDIA`
 * on the same build for exactly this reason. The ceiling has to sit well clear
 * of the slowest honest microphone, not just the typical one.
 */
export const VOICE_PATH_MIC_DEADLINE_MS = 5000;

/**
 * Put a remote stream on a sink and start it.
 *
 * Never throws. It runs on a live call: an element whose `play()` is rejected
 * by an autoplay policy, or a platform where `play()` returns no promise at all
 * (jsdom), are both "the audio is where it was" rather than a reason to take an
 * exception out of an effect. `play()`'s optional call is load-bearing for the
 * same reason.
 */
export function attachVoicePlayback(
  element: HTMLAudioElement | null | undefined,
  stream: MediaStream,
): boolean {
  if (!element) return false;
  try {
    element.srcObject = stream;
    const playing = element.play() as Promise<void> | undefined;
    void playing?.catch(() => {
      /* autoplay policy, or the element went away. */
    });
    return true;
  } catch {
    return false;
  }
}

/**
 * THE ONE AUDIO OUTPUT PATH A SESSION HAS, AND WHO IS ALLOWED TO OPEN IT.
 *
 * Everything above is about the partner's stream, but the platform's answer is
 * not per-element — it is per RENDERER. Chromium has ONE output stream for the
 * page, and whatever plays FIRST is what creates it and fixes its usage for
 * every sound that follows. Measured on the reference handset: with the gate
 * above in place and the partner correctly held, a 1:1 still came out
 * `USAGE_MEDIA` because the "you have been matched" chime played at connect —
 * before `getUserMedia` resolved — and the gated voice sink then joined the
 * stream the chime had already made.
 *
 *   sound effects muted   piid:9471 usage=USAGE_VOICE_COMMUNICATION   PASS
 *   sound effects on      piid:9711 usage=USAGE_MEDIA                 FAIL
 *
 * Same build, same handset, minutes apart. So holding the voice sink alone is
 * not a fix; the rule has to be that NOTHING the app plays gets in front of the
 * microphone while a call is coming up.
 *
 * WHY IT IS A MODULE AND NOT A PROP. The chime is played by the queue flow, the
 * partner by the call screen, and the session cues by a circle — three owners,
 * one output stream. A gate passed between them is a gate one of them forgets
 * (class D). This is the single place that knows whether the path is being held.
 *
 * WHAT IT IS NOT: a mute. A held sound is PLAYED, late — as soon as the call's
 * microphone is up, which is when it can be routed like part of the call. A
 * sound dropped here would be the match chime silently disappearing.
 */
let heldFor: number | null = null;
let queue: Array<() => void> = [];

/**
 * A call is coming up and has not got its microphone yet: hold anything that
 * would otherwise open the output stream in front of it.
 *
 * Keyed by session so a second call cannot release the first one's hold, and so
 * a stale release from a screen that has moved on is a no-op (class A).
 */
export function holdSessionAudio(sessionId: number): void {
  heldFor = sessionId;
}

/**
 * The microphone is up (or the deadline passed): let the path open, and let
 * everything that was waiting through in the order it arrived.
 */
export function releaseSessionAudio(sessionId: number): void {
  if (heldFor !== sessionId) return;
  heldFor = null;
  const waiting = queue;
  queue = [];
  for (const play of waiting) {
    try {
      play();
    } catch {
      /* one sound's failure is not the next sound's problem */
    }
  }
}

/** Is a call currently holding the output path? Exposed for tests and callers. */
export function isSessionAudioHeld(): boolean {
  return heldFor !== null;
}

/**
 * Play now, or as soon as the call's microphone is up.
 *
 * THE QUEUE IS BOUNDED, by two things rather than one. Once the call asks the
 * platform for a microphone, `VOICE_PATH_MIC_DEADLINE_MS` releases the path
 * whatever the answer is. Before that — a signalling POST still in flight, so
 * capture has not been requested yet — the bound is the CALL SCREEN: its layout
 * effect releases the hold on unmount, and the screen itself cannot outlive the
 * 30 s connection timeout. The deadline deliberately does NOT cover the
 * signalling that precedes capture: armed at mount it was spent before the
 * microphone was ever asked for, which let the partner's stream open the
 * renderer's output stream first — the whole defect this module exists to
 * prevent.
 */
export function onVoicePathReady(play: () => void): void {
  if (heldFor === null) {
    play();
    return;
  }
  queue.push(play);
}

/** Test seam: forget any hold and drop anything waiting on it. */
export function resetSessionAudioGate(): void {
  heldFor = null;
  queue = [];
}
